import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  AdminDashboardResponse,
  AdminEditableSettings,
  AdminJobDetail,
  AdminJobListResponse,
  AdminSession,
  AdminSettingsResponse,
  AdminUsersResponse,
  CreateJobRequest,
  JobStatusResponse,
  PlaylistSummary,
  SongSummary,
} from "../api/types";
import { TERMINAL_STATUSES } from "../api/types";
import { JobProgress } from "../components/JobProgress";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { PlaylistAddButton } from "../components/PlaylistAddButton";
import { UrlForm } from "../components/UrlForm";
import { navigate } from "../hooks/useLocation";
import { ADMIN_TABS, type AdminTab } from "./AdminPageTypes";

interface AdminShellProps {
  children: ReactNode;
}

export function AdminShell({ children }: AdminShellProps) {
  return (
    <main className="mx-auto flex min-h-screen max-w-6xl flex-col gap-6 px-4 py-8">
      {children}
    </main>
  );
}

export function AdminLoadingPanel() {
  const { t } = useTranslation();
  return (
    <AdminShell>
      <p className="text-sm text-stone-400">{t("admin.loading")}</p>
    </AdminShell>
  );
}

interface AdminLoginPanelProps {
  error: string | null;
  pending: boolean;
  onLogin: (password: string) => Promise<boolean>;
}

export function AdminLoginPanel({ error, pending, onLogin }: AdminLoginPanelProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (await onLogin(password)) setPassword("");
  }

  return (
    <AdminShell>
      <header className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-left text-3xl font-semibold text-stone-100 hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        >
          Bilgili Karaoke
        </button>
        <LanguageSwitcher />
      </header>
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-w-sm space-y-4 rounded-2xl border border-stone-800 bg-stone-900/60 p-5"
      >
        <h1 className="text-xl font-semibold text-stone-100">{t("admin.login.heading")}</h1>
        <label className="block space-y-2 text-sm text-stone-200">
          <span>{t("admin.login.password")}</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            autoComplete="current-password"
          />
        </label>
        <button
          type="submit"
          disabled={pending || password.length === 0}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-brand-950 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("admin.login.submit")}
        </button>
        {error && <p role="alert" className="text-sm text-red-200">{error}</p>}
      </form>
    </AdminShell>
  );
}

interface AdminHeaderProps {
  session: AdminSession;
  onLogout: () => Promise<void>;
}

export function AdminHeader({ session, onLogout }: AdminHeaderProps) {
  const { t } = useTranslation();
  return (
    <header className="flex items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="block min-w-0 truncate text-left text-3xl font-semibold text-stone-100 hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
        >
          Bilgili Karaoke Yönetim
        </button>
        <p className="min-w-0 truncate text-xs text-stone-500">
          {t("admin.sessionExpires", { expires: new Date(session.expires_at).toLocaleString() })}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <LanguageSwitcher />
        <button
          type="button"
          onClick={() => void onLogout()}
          className="rounded-lg border border-stone-700 px-3 py-2 text-sm text-stone-200 hover:bg-stone-800"
        >
          {t("admin.logout")}
        </button>
      </div>
    </header>
  );
}

interface AdminTabsProps {
  activeTab: AdminTab;
  onChange: (tab: AdminTab) => void;
}

export function AdminTabs({ activeTab, onChange }: AdminTabsProps) {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-wrap gap-2" aria-label={t("admin.tabs.label")}>
      {ADMIN_TABS.map((tab) => (
        <button
          type="button"
          key={tab}
          onClick={() => onChange(tab)}
          className={`rounded-lg border px-3 py-2 text-sm ${
            activeTab === tab
              ? "border-brand-500 bg-brand-500 text-brand-950"
              : "border-stone-700 text-stone-200 hover:bg-stone-800"
          }`}
        >
          {t(`admin.tabs.${tab}`)}
        </button>
      ))}
    </nav>
  );
}

export function AdminErrorMessage({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-lg border border-red-500 bg-red-500/10 p-3 text-sm text-red-100">
      {error}
    </p>
  );
}

interface OverviewPanelProps {
  dashboard: AdminDashboardResponse | null;
  onSelectJob: (jobId: string) => void;
}

export function OverviewPanel({ dashboard, onSelectJob }: OverviewPanelProps) {
  const { t } = useTranslation();
  return (
    <section className="grid gap-4 md:grid-cols-3">
      {Object.entries(dashboard?.job_counts ?? {}).map(([statusKey, count]) => (
        <div key={statusKey} className="rounded-xl border border-stone-800 bg-stone-900/60 p-4">
          <p className="text-xs uppercase text-stone-500">{statusKey}</p>
          <p className="text-3xl font-semibold text-stone-100">{count}</p>
        </div>
      ))}
      <div className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 md:col-span-3">
        <h2 className="mb-3 text-lg font-semibold text-stone-100">{t("admin.overview.recentJobs")}</h2>
        <div className="grid gap-2">
          {(dashboard?.recent_jobs ?? []).map((job) => (
            <button
              type="button"
              key={job.id}
              onClick={() => onSelectJob(job.id)}
              className="rounded-lg border border-stone-800 px-3 py-2 text-left text-sm text-stone-200 hover:bg-stone-800"
            >
              {job.source_title ?? job.youtube_video_id ?? job.youtube_url}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

interface JobsPanelProps {
  jobPlaylistMembership: ReadonlySet<string>;
  jobs: AdminJobListResponse | null;
  jobStatus: JobStatusResponse | null;
  pending: boolean;
  pendingPlaylistKey: string | null;
  playlists: PlaylistSummary[];
  search: string;
  selectedJob: AdminJobDetail | null;
  selectedSong: SongSummary | null;
  onAddToPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
  onCancelJob: () => Promise<void>;
  onCreateJob: (payload: CreateJobRequest) => Promise<void>;
  onDeleteJob: (jobId: string) => Promise<void>;
  onSearchChange: (search: string) => void;
  onSearchSubmit: () => Promise<void>;
  onSelectJob: (jobId: string) => Promise<void>;
}

export function JobsPanel({
  jobPlaylistMembership,
  jobs,
  jobStatus,
  pending,
  pendingPlaylistKey,
  playlists,
  search,
  selectedJob,
  selectedSong,
  onAddToPlaylist,
  onCancelJob,
  onCreateJob,
  onDeleteJob,
  onSearchChange,
  onSearchSubmit,
  onSelectJob,
}: JobsPanelProps) {
  const { t } = useTranslation();
  return (
    <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
      <div className="space-y-5">
        <UrlForm disabled={pending} onSubmit={(payload) => void onCreateJob(payload)} />
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void onSearchSubmit();
          }}
          className="flex gap-2"
        >
          <input
            value={search}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
            className="min-w-0 flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-sm text-stone-100"
            placeholder={t("admin.jobs.search")}
            aria-label={t("admin.jobs.search")}
          />
          <button type="submit" className="rounded-lg border border-stone-700 px-3 py-2 text-sm text-stone-200">
            {t("admin.jobs.searchButton")}
          </button>
        </form>
        <div className="space-y-2">
          {(jobs?.jobs ?? []).map((job) => (
            <button
              type="button"
              key={job.id}
              onClick={() => void onSelectJob(job.id)}
              className="w-full rounded-lg border border-stone-800 bg-stone-900/40 p-3 text-left hover:bg-stone-800"
            >
              <span className="block text-sm font-medium text-stone-100">
                {job.source_title ?? job.youtube_video_id ?? job.id}
              </span>
              <span className="text-xs text-stone-500">{job.status} · {job.progress}%</span>
            </button>
          ))}
        </div>
      </div>
      <JobDetailPanel
        jobPlaylistMembership={jobPlaylistMembership}
        jobStatus={jobStatus}
        pendingPlaylistKey={pendingPlaylistKey}
        playlists={playlists}
        selectedJob={selectedJob}
        selectedSong={selectedSong}
        onAddToPlaylist={onAddToPlaylist}
        onCancelJob={onCancelJob}
        onDeleteJob={onDeleteJob}
      />
    </section>
  );
}

interface JobDetailPanelProps {
  jobPlaylistMembership: ReadonlySet<string>;
  jobStatus: JobStatusResponse | null;
  pendingPlaylistKey: string | null;
  playlists: PlaylistSummary[];
  selectedJob: AdminJobDetail | null;
  selectedSong: SongSummary | null;
  onAddToPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
  onCancelJob: () => Promise<void>;
  onDeleteJob: (jobId: string) => Promise<void>;
}

function JobDetailPanel({
  jobPlaylistMembership,
  jobStatus,
  pendingPlaylistKey,
  playlists,
  selectedJob,
  selectedSong,
  onAddToPlaylist,
  onCancelJob,
  onDeleteJob,
}: JobDetailPanelProps) {
  const { t } = useTranslation();
  if (!selectedJob) {
    return (
      <aside className="space-y-4 rounded-xl border border-stone-800 bg-stone-900/60 p-4">
        <p className="text-sm text-stone-400">{t("admin.jobs.none")}</p>
      </aside>
    );
  }

  const title = selectedJob.source_title ?? selectedJob.youtube_video_id ?? selectedJob.youtube_url;
  const isTerminal = TERMINAL_STATUSES.has(selectedJob.status);
  const deleteJobId = selectedJob.id;

  function handleDeleteJob() {
    if (!window.confirm(t("admin.jobs.deleteConfirm"))) return;
    void onDeleteJob(deleteJobId);
  }

  return (
    <aside className="space-y-4 rounded-xl border border-stone-800 bg-stone-900/60 p-4">
      <h2 className="text-lg font-semibold text-stone-100">{title}</h2>
      <JobProgress
        status={jobStatus}
        isPolling={Boolean(jobStatus && !TERMINAL_STATUSES.has(jobStatus.status))}
      />
      <div className="flex flex-wrap gap-2">
        {!isTerminal && (
          <button
            type="button"
            onClick={() => void onCancelJob()}
            className="rounded-lg border border-red-400 px-3 py-2 text-sm text-red-100 hover:bg-red-500/10"
          >
            {t("admin.jobs.cancel")}
          </button>
        )}
        <button
          type="button"
          onClick={handleDeleteJob}
          disabled={!isTerminal}
          title={isTerminal ? undefined : t("admin.jobs.deleteNonTerminal")}
          className="rounded-lg border border-red-400 px-3 py-2 text-sm text-red-100 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("admin.jobs.delete")}
        </button>
        {selectedJob.status === "completed" && (
          <button
            type="button"
            onClick={() => navigate(`/songs/${selectedJob.id}`)}
            className="rounded-lg border border-stone-700 px-3 py-2 text-sm text-stone-200 hover:bg-stone-800"
          >
            {t("admin.jobs.openSong")}
          </button>
        )}
      </div>
      {selectedSong && selectedJob.status === "completed" && (
        <div className="flex flex-wrap gap-2">
          {playlists.map((playlist) => {
            const key = `${playlist.slug}:${selectedJob.id}`;
            return (
              <PlaylistAddButton
                key={playlist.slug}
                playlist={playlist}
                alreadyAdded={jobPlaylistMembership.has(playlist.slug)}
                isPending={pendingPlaylistKey === key}
                onAdd={(slug) => void onAddToPlaylist(slug, selectedJob.id)}
              />
            );
          })}
        </div>
      )}
      <div className="space-y-2">
        {selectedJob.steps.map((step) => (
          <div key={`${step.name}:${step.attempt}`} className="rounded-lg border border-stone-800 p-2 text-xs text-stone-300">
            {step.name} · {step.status} · {step.provider_used ?? "-"}
          </div>
        ))}
      </div>
    </aside>
  );
}

interface LibraryPanelProps {
  pendingPlaylistKey: string | null;
  playlists: PlaylistSummary[];
  recentSongs: SongSummary[];
  onAddToPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
  onDeleteSong: (jobId: string) => Promise<void>;
  onRemoveFromPlaylist: (playlistSlug: string, jobId: string) => Promise<void>;
}

export function LibraryPanel({
  pendingPlaylistKey,
  playlists,
  recentSongs,
  onAddToPlaylist,
  onDeleteSong,
  onRemoveFromPlaylist,
}: LibraryPanelProps) {
  const { t } = useTranslation();

  function confirmDeleteSong(jobId: string) {
    if (!window.confirm(t("admin.library.deleteSongConfirm"))) return;
    void onDeleteSong(jobId);
  }

  return (
    <section className="grid gap-6 md:grid-cols-2">
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-stone-100">{t("recent.heading")}</h2>
        {recentSongs.map((song) => (
          <div key={song.job_id} className="rounded-lg border border-stone-800 bg-stone-900/60 p-3">
            <button
              type="button"
              onClick={() => navigate(`/songs/${song.job_id}`)}
              className="text-left text-sm text-stone-100 hover:text-brand-300"
            >
              {titleFor(song)}
            </button>
            <div className="mt-2 flex flex-wrap gap-2">
              {playlists.map((playlist) => {
                const alreadyAdded = playlist.songs.some((entry) => entry.job_id === song.job_id);
                const key = `${playlist.slug}:${song.job_id}`;
                return (
                  <PlaylistAddButton
                    key={playlist.slug}
                    playlist={playlist}
                    alreadyAdded={alreadyAdded}
                    isPending={pendingPlaylistKey === key}
                    onAdd={(slug) => void onAddToPlaylist(slug, song.job_id)}
                  />
                );
              })}
              <button
                type="button"
                onClick={() => confirmDeleteSong(song.job_id)}
                className="rounded border border-red-400 px-2 py-1 text-xs text-red-100 hover:bg-red-500/10"
              >
                {t("admin.library.deleteSong")}
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-stone-100">{t("playlists.heading")}</h2>
        {playlists.map((playlist) => (
          <div key={playlist.slug} className="rounded-lg border border-stone-800 bg-stone-900/60 p-3">
            <h3 className="font-medium text-stone-100">{playlist.name}</h3>
            <div className="mt-2 space-y-2">
              {playlist.songs.map((song) => (
                <div key={song.job_id} className="flex items-center justify-between gap-3 text-sm text-stone-300">
                  <button
                    type="button"
                    onClick={() => navigate(`/songs/${song.job_id}`)}
                    className="min-w-0 flex-1 truncate text-left text-stone-100 hover:text-brand-300 hover:underline"
                  >
                    {titleFor(song)}
                  </button>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => void onRemoveFromPlaylist(playlist.slug, song.job_id)}
                      className="rounded border border-stone-700 px-2 py-1 text-xs hover:bg-stone-800"
                    >
                      {t("playlists.remove")}
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmDeleteSong(song.job_id)}
                      className="rounded border border-red-400 px-2 py-1 text-xs text-red-100 hover:bg-red-500/10"
                    >
                      {t("admin.library.deleteSong")}
                    </button>
                  </div>
                </div>
              ))}
              {playlist.songs.length === 0 && <p className="text-sm text-stone-500">{t("playlists.emptySongs")}</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function UsersPanel({ users }: { users: AdminUsersResponse | null }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-2">
      {(users?.users ?? []).map((user) => (
        <div key={user.id} className="rounded-lg border border-stone-800 bg-stone-900/60 p-3 text-sm text-stone-200">
          <p className="font-mono text-xs text-stone-400">{user.id}</p>
          <p>{user.email ?? t("admin.users.system")}</p>
          <p>{t("admin.users.jobCount", { count: user.job_count })}</p>
        </div>
      ))}
    </section>
  );
}

interface SettingsPanelProps {
  pending: boolean;
  settings: AdminSettingsResponse | null;
  onSave: (payload: AdminEditableSettings) => Promise<void>;
}

export function SettingsPanel({ pending, settings, onSave }: SettingsPanelProps) {
  const { t } = useTranslation();
  if (!settings) return null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!settings) return;
    void onSave(settingsPayload(new FormData(event.currentTarget), settings.editable));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        <NumberField name="max_source_duration_seconds" label={t("admin.settings.maxDuration")} value={settings.editable.max_source_duration_seconds} />
        <NumberField name="lyric_line_max_chars" label={t("admin.settings.lineChars")} value={settings.editable.lyric_line_max_chars} />
        <NumberField name="lyric_line_min_words" label={t("admin.settings.lineMinWords")} value={settings.editable.lyric_line_min_words} />
        <NumberField name="lyric_line_max_words" label={t("admin.settings.lineWords")} value={settings.editable.lyric_line_max_words} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <SelectField name="provider_metadata" label={t("admin.settings.metadata")} value={settings.editable.provider_metadata} options={settings.provider_options.metadata} />
        <SelectField name="provider_audio_extraction" label={t("admin.settings.audio")} value={settings.editable.provider_audio_extraction} options={settings.provider_options.audio_extraction} />
        <SelectField name="provider_separation" label={t("admin.settings.separation")} value={settings.editable.provider_separation} options={settings.provider_options.separation} />
        <SelectField name="provider_transcription" label={t("admin.settings.transcription")} value={settings.editable.provider_transcription} options={settings.provider_options.transcription} />
        <SelectField name="provider_subtitles" label={t("admin.settings.subtitles")} value={settings.editable.provider_subtitles} options={settings.provider_options.subtitles} />
        <SelectField name="lalalai_splitter" label={t("admin.settings.splitter")} value={settings.editable.lalalai_splitter} options={settings.provider_options.lalalai_splitters} />
      </div>
      <label className="block space-y-2 text-sm text-stone-200">
        <span>{t("admin.settings.deepgramModel")}</span>
        <input name="deepgram_model" defaultValue={settings.editable.deepgram_model} className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100" />
      </label>
      <div className="flex flex-wrap gap-4">
        <CheckboxField name="karaoke_word_highlighting" label={t("admin.settings.wordHighlighting")} checked={settings.editable.karaoke_word_highlighting} />
        <CheckboxField name="lalalai_dereverb" label={t("admin.settings.dereverb")} checked={settings.editable.lalalai_dereverb} />
      </div>
      <div className="rounded-lg border border-stone-800 bg-stone-900/60 p-3 text-sm text-stone-300">
        {t("admin.settings.credentials", {
          deepgram: settings.read_only.deepgram_api_key_configured ? t("admin.settings.configured") : t("admin.settings.missing"),
          lalalai: settings.read_only.lalal_ai_api_key_configured ? t("admin.settings.configured") : t("admin.settings.missing"),
        })}
      </div>
      <button type="submit" disabled={pending} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-brand-950 disabled:opacity-50">
        {t("admin.settings.save")}
      </button>
    </form>
  );
}

interface FieldProps {
  name: keyof AdminEditableSettings;
  label: string;
}

function NumberField({ name, label, value }: FieldProps & { value: number }) {
  return (
    <label className="block space-y-2 text-sm text-stone-200">
      <span>{label}</span>
      <input
        name={name}
        type="number"
        defaultValue={value}
        className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100"
      />
    </label>
  );
}

function SelectField({ name, label, value, options }: FieldProps & { value: string; options: string[] }) {
  return (
    <label className="block space-y-2 text-sm text-stone-200">
      <span>{label}</span>
      <select
        name={name}
        defaultValue={value}
        className="w-full rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100"
      >
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function CheckboxField({ name, label, checked }: FieldProps & { checked: boolean }) {
  return (
    <label className="flex items-center gap-2 text-sm text-stone-200">
      <input name={name} type="checkbox" defaultChecked={checked} className="h-4 w-4" />
      <span>{label}</span>
    </label>
  );
}

function titleFor(song: SongSummary): string {
  return song.title || song.job_id;
}

function settingNumber(
  value: FormDataEntryValue | null,
  fallback: number,
): number {
  if (typeof value !== "string") return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function settingsPayload(
  formData: FormData,
  current: AdminEditableSettings,
): AdminEditableSettings {
  return {
    max_source_duration_seconds: settingNumber(
      formData.get("max_source_duration_seconds"),
      current.max_source_duration_seconds,
    ),
    lyric_line_max_chars: settingNumber(
      formData.get("lyric_line_max_chars"),
      current.lyric_line_max_chars,
    ),
    lyric_line_max_words: settingNumber(
      formData.get("lyric_line_max_words"),
      current.lyric_line_max_words,
    ),
    lyric_line_min_words: settingNumber(
      formData.get("lyric_line_min_words"),
      current.lyric_line_min_words,
    ),
    karaoke_word_highlighting: formData.get("karaoke_word_highlighting") === "on",
    provider_metadata: String(formData.get("provider_metadata") ?? current.provider_metadata),
    provider_audio_extraction: String(
      formData.get("provider_audio_extraction") ?? current.provider_audio_extraction,
    ),
    provider_separation: String(
      formData.get("provider_separation") ?? current.provider_separation,
    ),
    provider_transcription: String(
      formData.get("provider_transcription") ?? current.provider_transcription,
    ),
    provider_subtitles: String(formData.get("provider_subtitles") ?? current.provider_subtitles),
    deepgram_model: String(formData.get("deepgram_model") ?? current.deepgram_model),
    lalalai_splitter: String(formData.get("lalalai_splitter") ?? current.lalalai_splitter),
    lalalai_dereverb: formData.get("lalalai_dereverb") === "on",
  };
}
